"use client";
import React from "react";
import { Box, Typography, Container } from "@mui/material";
import NextLink from "next/link";
import { useTranslation } from "react-i18next";
import { IMAGES } from "../constants/images";

// route each agent card links to
const AGENT_ROUTES = {
  ZAYN: "/ai-agents/zayn",
  SCOUT: "/ai-agents/scout",
  SAGE: "/ai-agents/sage",
  ATLAS: "/ai-agents/atlas",
  COMPASS: "/ai-agents/compass",
  GOKU: "/ai-agents/omni", // GOKU = OMNI
};

const AgentCard = ({ agent, href }) => (
  <Box
    component={NextLink}
    href={href}
    sx={{
      display: "flex",
      flexDirection: "column",
      alignItems: "flex-start",
      gap: 0.5,
      px: { xs: 2, sm: 2.25 },
      py: { xs: 1.5, sm: 1.75 },
      borderRadius: "14px",
      backgroundColor: "#fff",
      border: "1px solid rgba(0,0,0,0.06)",
      boxShadow: "0 2px 8px rgba(20,20,40,0.05)",
      transition: "all 0.25s ease",
      cursor: "pointer",
      width: "100%",
      textDecoration: "none",
      color: "inherit",
      "&:hover": {
        transform: "translateY(-2px)",
        boxShadow: "0 8px 20px rgba(120,80,200,0.12)",
      },
    }}
  >
    <Box
      component="img"
      src={agent.image}
      alt={agent.name}
      sx={{
        width: { xs: 34, sm: 38, md: 42 },
        height: { xs: 34, sm: 38, md: 42 },
        borderRadius: "50%",
        objectFit: "cover",
        flexShrink: 0,
        mb: 0.75,
      }}
    />
    <Typography
      sx={{
        fontFamily: "var(--font-outfit)",
        fontWeight: 700,
        fontSize: { xs: "0.85rem", sm: "0.9rem", md: "0.95rem" },
        color: "#1a1a2e",
        textAlign: "left",
      }}
    >
      {agent.name}
    </Typography>
    <Typography
      sx={{
        fontFamily: "var(--font-outfit)",
        fontWeight: 400,
        fontSize: { xs: "0.68rem", sm: "0.72rem", md: "0.76rem" },
        color: "#9fa4b4",
        textAlign: "left",
        lineHeight: 1.3,
      }}
    >
      {agent.role}
    </Typography>
  </Box>
);

const OnePlatformSection = () => {
  const { t } = useTranslation();

  const agentKeys = ["ZAYN", "SCOUT", "SAGE", "ATLAS", "COMPASS", "GOKU"];
  const keyToImage = {
    ZAYN: IMAGES.ZAYN_CIRCLE,
    ATLAS: IMAGES.ATLAS_CIRCLE,
    SAGE: IMAGES.SAGE_CIRCLE,
    SCOUT: IMAGES.SCOUT_CIRCLE,
    COMPASS: IMAGES.COMPASS_CIRCLE,
    GOKU: IMAGES.GOKU_CIRCLE,
  };

  const AGENTS = agentKeys.map((key) => ({
    key,
    name: t(`aiAgentsSection.agents.${key}.name`),
    role: t(`aiAgentsSection.agents.${key}.role`),
    image: keyToImage[key],
  }));

  return (
    <Box
      sx={{
        backgroundColor: "#f6f2fc",
        py: { xs: 5, sm: 7, md: 10 },
        px: { xs: 1.5, sm: 2 },
        overflow: "hidden",
      }}
    >
      <Container maxWidth="lg">
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            alignItems: { xs: "flex-start", md: "center" },
            gap: { xs: 4, md: 10 },
          }}
        >
          {/* Left: heading + copy */}
          <Box
            sx={{
              flex: { md: "0 0 40%" },
              maxWidth: { md: "40%" },
            }}
          >
            <Typography
              sx={{
                fontFamily: "var(--font-plusjakarta)",
                fontWeight: 800,
                fontSize: { xs: "1.6rem", sm: "2rem", md: "2.4rem" },
                color: "#1a1a2e",
                lineHeight: 1.25,
              }}
            >
              {t("onePlatform.title")}{" "}
              <Box component="span" sx={{ color: "#9810FA" }}>
                {t("onePlatform.titleHighlight")}
              </Box>
            </Typography>
            <Typography
              sx={{
                fontFamily: "var(--font-outfit)",
                fontSize: { xs: "0.85rem", sm: "0.95rem", md: "1rem" },
                color: "#9fa4b4",
                lineHeight: 1.6,
                mt: 2,
              }}
            >
              {t("onePlatform.subtitle")}
            </Typography>
          </Box>

          {/* Right: agent cards grid */}
          <Box
            sx={{
              flex: 1,
              display: "grid",
              gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
              gap: { xs: 1.5, sm: 2 },
              width: "100%",
            }}
          >
            {AGENTS.map((agent) => (
              <AgentCard
                key={agent.key}
                agent={agent}
                href={AGENT_ROUTES[agent.key]}
              />
            ))}
          </Box>
        </Box>
      </Container>
    </Box>
  );
};

export default OnePlatformSection;